import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CreditCard, Check, Loader2, CalendarClock, ShieldCheck } from 'lucide-react';

import { Spinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/store/auth.store';
import {
  getMe,
  getPublicPlans,
  createCheckout,
  cancelSubscription,
  cancelScheduledChange,
  fmtKop,
  fmtRub,
  type Period,
  type SubscriptionStatus,
} from '@/services/billing';
import { formatDate } from '@/utils/format';

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  TRIAL: 'Пробный период',
  ACTIVE: 'Активна',
  PAST_DUE: 'Ожидает оплаты',
  CANCELLED: 'Отменена',
};
const STATUS_TONE: Record<SubscriptionStatus, string> = {
  TRIAL: 'bg-sky-50 text-sky-700 ring-sky-200',
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAST_DUE: 'bg-amber-50 text-amber-700 ring-amber-200',
  CANCELLED: 'bg-rose-50 text-rose-700 ring-rose-200',
};
const PAYMENT_STATUS: Record<string, string> = {
  PENDING: 'Ожидает',
  SUCCEEDED: 'Оплачено',
  FAILED: 'Ошибка',
  CANCELLED: 'Отменён',
  WAITING_FOR_CAPTURE: 'В обработке',
  REFUNDED: 'Возврат',
};
const PERIOD_LABEL: Record<Period, string> = { MONTHLY: 'Месяц', YEARLY: 'Год' };

const dateOnly = { hour: undefined, minute: undefined } as const;
const limit = (n: number | null) => (n == null ? '∞' : String(n));

function LimitBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export default function SubscriptionPage() {
  const role = useAuthStore((s) => s.user?.role);
  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>('MONTHLY');

  const isOwner = role === 'OWNER';
  const { data: me, isLoading } = useQuery({
    queryKey: ['billing/me'],
    queryFn: getMe,
    enabled: isOwner,
  });
  const { data: plansData } = useQuery({
    queryKey: ['billing/plans'],
    queryFn: getPublicPlans,
    enabled: isOwner,
  });

  const checkoutMut = useMutation({
    mutationFn: (vars: { planCode?: string; billingPeriod?: Period }) =>
      createCheckout({ ...vars, returnUrl: window.location.href }),
    onSuccess: (res) => {
      window.location.href = res.confirmationUrl;
    },
    // Ошибки (402/400/5xx) показывает глобальный axios-перехватчик (toast).
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelSubscription(),
    onSuccess: () => {
      toast.success('Авто-продление отключено');
      qc.invalidateQueries({ queryKey: ['billing/me'] });
    },
  });

  const cancelSchedMut = useMutation({
    mutationFn: () => cancelScheduledChange(),
    onSuccess: () => {
      toast.success('Запланированная смена отменена');
      qc.invalidateQueries({ queryKey: ['billing/me'] });
    },
  });

  if (!isOwner) {
    return (
      <div>
        <header className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold">Подписка и оплата</h1>
          <p className="text-sm text-slate-500">Тариф, лимиты и история платежей</p>
        </header>
        <div className="card p-12 text-center text-slate-400">
          <ShieldCheck size={28} className="mx-auto mb-2 opacity-40" />
          Раздел доступен только владельцу заведения.
        </div>
      </div>
    );
  }

  const sub = me?.subscription ?? null;
  const plans = plansData?.plans ?? [];
  const currentCode = sub?.planDetails?.code ?? sub?.plan ?? null;
  const busy = checkoutMut.isPending;

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Подписка и оплата</h1>
        <p className="text-sm text-slate-500">Тариф, лимиты и история платежей</p>
      </header>

      {isLoading && <Spinner />}

      {!isLoading && (
        <div className="space-y-4">
          {/* Текущая подписка */}
          {sub ? (
            <div className="card p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <CreditCard size={18} className="text-brand-600" />
                    <span className="text-lg font-semibold">
                      {sub.planDetails?.name ?? 'Без тарифа'}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ring-1 ${STATUS_TONE[sub.status]}`}
                    >
                      {STATUS_LABEL[sub.status]}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    {PERIOD_LABEL[sub.billingPeriod]}
                    {sub.amount > 0 && ` · ${fmtKop(sub.amount)}`}
                  </div>
                </div>
                <div className="text-right text-sm">
                  {sub.status === 'TRIAL' && sub.trialEndsAt && (
                    <div className="text-slate-500">
                      Пробный до{' '}
                      <b className="text-slate-700">{formatDate(sub.trialEndsAt, dateOnly)}</b>
                    </div>
                  )}
                  {sub.currentPeriodEnd && (
                    <div className="text-slate-500">
                      Оплачено до{' '}
                      <b className="text-slate-700">{formatDate(sub.currentPeriodEnd, dateOnly)}</b>
                    </div>
                  )}
                  {sub.daysLeft != null && (
                    <div className="text-slate-400 text-xs mt-0.5">осталось {sub.daysLeft} дн.</div>
                  )}
                </div>
              </div>

              {sub.planDetails && (
                <div className="mt-4 grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                  <LimitBox label="Заведений" value={limit(sub.planDetails.maxEstablishments)} />
                  <LimitBox label="Касс" value={limit(sub.planDetails.maxCashiers)} />
                  <LimitBox label="Сотрудников" value={limit(sub.planDetails.maxUsers)} />
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  className="btn-primary"
                  disabled={busy}
                  onClick={() =>
                    checkoutMut.mutate({
                      planCode: currentCode ?? undefined,
                      billingPeriod: sub.billingPeriod,
                    })
                  }
                >
                  {busy ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CreditCard size={16} />
                  )}
                  {sub.status === 'TRIAL' ? 'Оплатить подписку' : 'Продлить'}
                </button>
                {sub.status !== 'CANCELLED' && !sub.cancelledAt && (
                  <button
                    className="btn-outline text-rose-600"
                    disabled={cancelMut.isPending}
                    onClick={() => {
                      if (
                        confirm(
                          'Отключить авто-продление? Доступ сохранится до конца оплаченного периода.',
                        )
                      ) {
                        cancelMut.mutate();
                      }
                    }}
                  >
                    Отменить авто-продление
                  </button>
                )}
              </div>
              {sub.cancelledAt && (
                <p className="mt-2 text-xs text-amber-600">
                  Авто-продление отключено. Доступ — до конца оплаченного периода.
                </p>
              )}
            </div>
          ) : (
            <div className="card p-5 text-slate-500">
              Активной подписки нет. Выберите тариф ниже, чтобы активировать её.
            </div>
          )}

          {/* Запланированная смена тарифа */}
          {sub?.scheduledChange && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <CalendarClock size={16} className="shrink-0" />
                <span>
                  С {formatDate(sub.scheduledChange.applyAt, dateOnly)} тариф сменится на «
                  {sub.scheduledChange.planName}» ({PERIOD_LABEL[sub.scheduledChange.billingPeriod]}
                  ).
                </span>
              </div>
              <button
                className="btn-outline"
                disabled={cancelSchedMut.isPending}
                onClick={() => cancelSchedMut.mutate()}
              >
                Отменить смену
              </button>
            </div>
          )}

          {/* Доступные тарифы */}
          <div className="card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-semibold">Сменить тариф</h2>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-sm">
                <button
                  type="button"
                  onClick={() => setPeriod('MONTHLY')}
                  className={
                    period === 'MONTHLY'
                      ? 'px-3 py-1 rounded-md bg-brand-600 text-white'
                      : 'px-3 py-1 text-slate-600'
                  }
                >
                  Месяц
                </button>
                <button
                  type="button"
                  onClick={() => setPeriod('YEARLY')}
                  className={
                    period === 'YEARLY'
                      ? 'px-3 py-1 rounded-md bg-brand-600 text-white'
                      : 'px-3 py-1 text-slate-600'
                  }
                >
                  Год
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {plans.map((p) => {
                const isCurrent = p.code === currentCode;
                const price = period === 'YEARLY' ? p.priceYear : p.priceMonth;
                return (
                  <div
                    key={p.code}
                    className={`rounded-xl border p-4 flex flex-col ${
                      p.isPopular ? 'border-brand-300' : 'border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{p.name}</span>
                      {isCurrent && <span className="text-xs text-emerald-600">Текущий</span>}
                    </div>
                    {p.tagline && <div className="mt-1 text-sm text-slate-500">{p.tagline}</div>}
                    <div className="mt-2 text-xl font-semibold">
                      {fmtRub(price)}
                      <span className="text-sm font-normal text-slate-400">
                        /{period === 'YEARLY' ? 'год' : 'мес'}
                      </span>
                    </div>
                    <ul className="mt-3 space-y-1 text-xs text-slate-600 flex-1">
                      {p.highlights.slice(0, 4).map((h, i) => (
                        <li key={i} className="flex gap-1.5">
                          <Check size={13} className="text-brand-500 mt-0.5 shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                    <button
                      className={`mt-4 w-full ${isCurrent ? 'btn-outline' : 'btn-primary'}`}
                      disabled={busy}
                      onClick={() => checkoutMut.mutate({ planCode: p.code, billingPeriod: period })}
                    >
                      {isCurrent ? 'Продлить' : 'Перейти'}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-slate-400">
              Оплата через ЮKassa. Для тарифа Network доплата за дополнительные заведения
              рассчитается на странице оплаты.
            </p>
          </div>

          {/* История платежей */}
          {sub && sub.payments.length > 0 && (
            <div className="card p-5">
              <h2 className="font-semibold mb-3">История платежей</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 text-xs">
                      <th className="py-2 font-medium">Дата</th>
                      <th className="font-medium">Сумма</th>
                      <th className="font-medium">Статус</th>
                      <th className="font-medium">Способ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sub.payments.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100">
                        <td className="py-2">{formatDate(p.paidAt ?? p.createdAt)}</td>
                        <td>{fmtKop(p.amount)}</td>
                        <td>{PAYMENT_STATUS[p.status] ?? p.status}</td>
                        <td className="text-slate-500">{p.method ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
